import { ChangeEvent, useEffect, useReducer, useState } from "react";
import { Header, CustomTable, WarningDialog } from "../../components";
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Manage from "./manage";
import "./index.scss";
import EditIcon from "@mui/icons-material/Edit";
import { withPermission } from "../../components/hoc";
import { getStaticTexts, PERMISSIONS } from "../../constants";
import {
  deleteCategories,
  getCategories,
  updateCategoryStatus,
} from "../../services/aggregation";
import { enqueueSnackbar } from "notistack";
import { SelectChangeEvent } from "@mui/material";
import Loader from "../../components/Loader";
import { useSelector } from "react-redux";
import { formatDate } from "../../services/utils";

interface state {
  manage: {
    isOpen: boolean;
    type: "new" | "edit" | "view";
    category: any;
  };
  approvalModal: {
    isOpen: boolean;
  };
  deleteWarning: boolean;
  multiDeleteWarning: boolean;
  _category: string;
  list: {
    all: JSX.Element;
    id: number;
    _id: string;
    name: JSX.Element;
    createdAt: JSX.Element;
    description: string;
    status: JSX.Element;
    action: JSX.Element;
  }[];
  selectAll: string[];
  statusFilter: string;
  loading: boolean;
  searchQuery: string;
  page: number;
  totalPages: number;
}

const Items = () => {
  const categoryReducer = (state: state, action: any): state => {
    switch (action.type) {
      case "LOADING":
        return { ...state, loading: true };
      case "LOADED":
        return { ...state, loading: false, list: action.list || [] };
      case "UPDATE_FIELDS":
        return { ...state, ...action.payload };
      case 'UPDATE_LIST':
        const categoryIds = action.payload;

        const filteredList = state.list.filter((category: any) => !(categoryIds || []).includes(category._id))
          .map((user: any, index: number) => ({
            ...user,
            id: (state.page - 1) * 10 + index + 1,
          }));

        return {
          ...state,
          list: filteredList,
        };
      case "UPDATE_STATUS":
        return {
          ...state,
          list: state.list.map((category: any) =>
            category._id === action._id
              ? { ...category, status: action.status }
              : category
          ),
        };
        case "UPDATE_CHECKBOX": {
          const { id, isChecked } = action.payload;
    
          // Update the `selectAll` array
          const updatedSelectAll = isChecked
            ? [...state.selectAll, id]
            : state.selectAll.filter((ele) => ele !== id);
    
          // Update the list to reflect the checkbox state
          const updatedList = state.list.map((category: any) => ({
            ...category,
            all: (
              <Checkbox
                onChange={(e) => handleMultipleSelect(e, state.selectAll, category._id)}
                checked={updatedSelectAll.includes(category._id)}
              />
            ),
          }));
          return {
            ...state,
            selectAll: updatedSelectAll,
            list: updatedList,
          };
        }
        case 'TOGGLE_STATUS':
          const { id, status } = action.payload;
          const updatedList = state.list.map((user: any) =>
            user._id === id
              ? {
                  ...user,
                  status: (
                    <Switch
                      checked={status === "ACTIVE"}
                      disabled={isEditIconDisabled}
                      onChange={(e) => {
                        handleStatusChange(
                          user._id,
                          e.target.checked ? "ACTIVE" : "INACTIVE",
                        );
                      }}
                    />
                  ),
                }
              : user
          );
          return {
            ...state,
            list: updatedList,
          };
           
      default:
        throw new Error(`Unhandled action type: ${action.type}`);
    }
  };

  const language = useSelector((state: any) => state.profile.defaultLanguage);
  const staticTexts = getStaticTexts(language);

  const userPermissions = useSelector((state: any) => state.permissions);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isEditIconDisabled = !userPermissions.includes(
    PERMISSIONS.EDIT_ITEMS_CATEGORY
  );
  const isDeleteIconDisabled = !userPermissions.includes(
    PERMISSIONS.DELETE_ITEMS_CATEGORY
  );

  const statusFilter = [
    {
      label: staticTexts.all,
      value: "ALL",
    },
    {
      label: staticTexts.active,
      value: "ACTIVE",
    },
    {
      label: staticTexts.inactive,
      value: "INACTIVE",
    },
  ];

  const fetchCategories = async (searchQuery: string, page: number = 1) => {
    try {
      const params: any = {
        page,
        limit: itemsPerPage,
        text: searchQuery,
      };
      if (state.statusFilter !== "ALL") {
        params.status = state.statusFilter;
      }

      const categories = (await getCategories(params)) as any;

      if (!categories || !categories?.categories?.length) {
        dispatch({ type: "LOADED", list: [] });
        return;
      }

      dispatch({
        type: "UPDATE_FIELDS",
        payload: { totalPages: categories?.meta.totalPages },
      });

      const list = categories?.categories.map((category: any, index: number) =>
        createRow(
          state.selectAll,
          category,
          index,
          handleManage,
          handleWarningDialog,
          handleMultipleSelect,
          page
        )
      );

      dispatch({ type: "LOADED", list });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const [state, dispatch] = useReducer(categoryReducer, {
    manage: {
      type: "new",
      isOpen: false,
      category: null,
    },
    approvalModal: {
      isOpen: false,
    },
    deleteWarning: false,
    multiDeleteWarning: false,
    _category: "",
    list: [],
    selectAll: [],
    loading: true,
    searchQuery: "",
    page: 1,
    statusFilter: "",
    totalPages: 1,
  });

  const handleSearch = (e: ChangeEvent<HTMLElement>) => {
    const searchQuery = (e.target as HTMLInputElement).value;
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { searchQuery, page: 1 },
    });
    fetchCategories(searchQuery, 1);
  };

  const onPageChange = (e: ChangeEvent<unknown>, page: number) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { page },
    });
    fetchCategories(state.searchQuery, page);
  };

  const onStatusFilterChange = (e: SelectChangeEvent) => {
    const newStatusFilter = e.target.value;
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { statusFilter: newStatusFilter },
    });
    fetchCategories(state.searchQuery, state.page);
  };

  useEffect(() => {
    fetchCategories(state.searchQuery, state.page);
  }, [state.statusFilter]);

  const handleManage = (
    type: "new" | "edit" | "view",
    category: any = null
  ) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        manage: {
          ...state.manage,
          isOpen: !state.manage.isOpen,
          type,
          category,
        },
      },
    });
  };

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const allIds = state.list.map((category: any) => category._id);

    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        selectAll: checked ? allIds : [],
      },
    });

    const updatedList = state.list.map((category: any) => ({
      ...category,
      all: (
        <Checkbox
          onChange={(e) =>
            handleMultipleSelect(e, checked ? allIds : [], category._id)
          }
          checked={checked}
        />
      ),
    }));

    dispatch({ type: "LOADED", list: updatedList });
  };

  const handleMultipleSelect = (
    e: ChangeEvent<HTMLInputElement>,
    selectAll: string[],
    id: string
  ) => {
    const isChecked = e.target.checked;
    dispatch({
      type: "UPDATE_CHECKBOX",
      payload: { id, isChecked },
    });
  };

  
  const handleDeleteCategory = async (categoryIds: Array<string>) => {
    try {
      await deleteCategories(categoryIds);
      enqueueSnackbar(staticTexts.categoryDeletedSuccessfully, {
        variant: "success",
      });
      dispatch({ type: 'UPDATE_LIST', payload: categoryIds });
    } catch (error: any) {
      enqueueSnackbar(
        error.response.data.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const handleMultiDelete = async (categoryIds: Array<string> = []) => {
    if (categoryIds?.length > 0) {
      await handleDeleteCategory(categoryIds);
      dispatch({
        type: "UPDATE_FIELDS",
        payload: {
          multiDeleteWarning: false,
        },
      });
    }
  };

  const handleDelete = async (categoryId: any) => {
    await handleDeleteCategory([categoryId]);
    dispatch({
      type: "UPDATE_FIELDS",
      payload: { deleteWarning: !state.deleteWarning, _category: "" },
    });
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateCategoryStatus(id, { status });
      enqueueSnackbar(staticTexts.categoryStatusUpdatedSuccessfully, {
        variant: "success",
      });
      dispatch({ type: 'TOGGLE_STATUS', payload: { id, status } });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          staticTexts.somethingWentWrong,
        {
          variant: "error",
        }
      );
    }
  };

  const handleWarningDialog = (_category: string) => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        deleteWarning: !state.deleteWarning,
        _category,
      },
    });
  };

  const handleMutlipleWarningDialog = () => {
    dispatch({
      type: "UPDATE_FIELDS",
      payload: {
        multiDeleteWarning: !state.multiDeleteWarning,
        selectAll: state.selectAll,
      },
    });
  };

  const columns = [
    {
      id: "all",
      label: (
        <Checkbox
          onChange={handleSelectAll}
          checked={
            state.selectAll?.length > 0 &&
            state.list?.length === state.selectAll?.length
          }
          indeterminate={
            state.selectAll?.length > 0 &&
            state.list?.length !== state.selectAll?.length
          }
        />
      ),
    },
    {
      id: "id",
      label: staticTexts.serialNumber,
    },
    {
      id: "name",
      label: staticTexts.categoryName,
    },
    {
      id: "createdAt",
      label: staticTexts.createdAt,
    },
    {
      id: "status",
      label: staticTexts.status,
    },
    {
      id: "action",
      label: staticTexts.action,
    },
  ];

  const createRow = (
    selectAll: string[],
    category: any,
    index: number,
    onEdit: any,
    onDelete: any,
    onMultiDelete: any,
    page: number
  ) => {
    return {
      all: (
        <Checkbox
          onChange={(e) => onMultiDelete(e, selectAll, category._id)}
          checked={state.selectAll.includes(category._id) ? true : false}
        />
      ),
      id: (page - 1) * itemsPerPage + index + 1,
      _id: category._id,
      name: (
        <Typography
          className="cursor-pointer"
          color="primary"
          variant="body1"
          onClick={() => onEdit("view", category)}
          sx={{ textTransform: "capitalize" }}
        >
          {category.name?.[language]}
        </Typography>
      ),
      createdAt: (
        <Typography className="cursor-pointer" color="primary" variant="body1">
          {formatDate(category.createdAt)}
        </Typography>
      ),
      status: (
        <Switch
          checked={category.status === "ACTIVE"}
          disabled={isEditIconDisabled}
          onChange={(e) => {
            e.target.checked
              ? handleStatusChange(category._id, "ACTIVE")
              : handleStatusChange(category._id, "INACTIVE");
          }}
        />
      ),
      action: [
        <Tooltip title={staticTexts.edit}>
          <IconButton
            onClick={() => onEdit("edit", category)}
            color="primary"
            disabled={isEditIconDisabled}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip title={staticTexts.delete}>
          <IconButton
            onClick={() => onDelete(category._id)}
            color="error"
            disabled={isDeleteIconDisabled}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>,
      ],
    };
  };

  return (
    <>
      {state.loading && <Loader />}
      <Header
        isEditIconDisabled={isEditIconDisabled}
        isDeleteIconDisabled={isDeleteIconDisabled}
        searchPlaceholder={staticTexts.searchByCategoryName}
        btnText={staticTexts.addNewCategory}
        onSearch={handleSearch}
        onBtnClick={() => handleManage("new")}
        onDelete={(e) => handleMutlipleWarningDialog()}
        isDeleteDisable={state.selectAll.length ? false : true}
        searchInput
      >
        <FormControl className="ml-2" size="small">
          <Select
            size="small"
            displayEmpty
            value={state.statusFilter}
            renderValue={(selected) => {
              if (!selected || selected === "") {
                return staticTexts.statusFilter;
              }
              if (selected === "ALL") {
                return staticTexts.all;
              }
              return selected === "ALL"
                ? staticTexts.all
                : selected === "ACTIVE"
                ? staticTexts.active
                : staticTexts.inactive;
            }}
            onChange={(e) => {
              dispatch({
                type: "UPDATE_FIELDS",
                payload: { statusFilter: e.target.value, page: 1 },
              });
            }}
          >
            {statusFilter.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Header>

      {/* Show Data  */}
      <Box marginTop="10px">
        <CustomTable
          columns={columns}
          rows={state.list}
          height="calc(100vh - 193px)"
          errorMessage={staticTexts.addCategoryToSeeData}
          pagination={{
            page: state.page,
            totalPages: state.totalPages,
          }}
          onPageChange={onPageChange}
        />
      </Box>

      {/* Manage Data  */}
      <Manage
        isOpen={state.manage.isOpen}
        type={state.manage.type}
        category={state.manage.category}
        onClose={() => handleManage(state.manage.type)}
      />

      {/* Delete Data  */}
      <WarningDialog
        isOpen={state.deleteWarning}
        onClose={() => handleWarningDialog(state._category || "")}
        onConfirm={() => handleDelete(state._category)}
        title={staticTexts.deleteCategory}
        description={staticTexts.areYouSureDoYouWantToDeleteCategory}
      />

      {/* Multi Delete Data  */}
      <WarningDialog
        isOpen={state.multiDeleteWarning}
        onClose={() => handleMutlipleWarningDialog()}
        onConfirm={() => handleMultiDelete(state.selectAll)}
        title={staticTexts.deleteCategories}
        description={staticTexts.areYouSureDoYouWantToDeleteCategory}
      />
    </>
  );
};

export default withPermission(Items, PERMISSIONS.VIEW_ITEMS_CATEGORY);
